import { useMemo, useState } from "react";
import { Incoterm } from "@/components/shared/incoterm";
import { ActionDataBo } from "@/types/tableAction";
import { useCreateQuotationBo } from "@/api/expedition.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { NotificationModal } from "../../shared/modal/notification.modal";
import { NotesModal } from "../../shared/modal/notes.modal";
import { Button } from "@/components/ui/button";
import {
  CircleCheck,
  CircleX,
  FileText,
  Loader2,
  MessageCircleMore,
  NotebookPen,
  Bell,
  X,
  Save,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InformationDisplay, ProtectedInformation } from "./informationDisplay";
import { TypeEnvoi } from "@/components/shared/badge/typeEnvoi.badge";
import { InfoClientsBo } from "./infoClientBo";
import { InfoBottlesBo } from "./infoBottlesBo";
import { InfoPackagingBo } from "./infoPackagingBo";
import { InfoValidationBo } from "./infoValidationBo";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import { QuotationUpdateBoDefaultValues, ReQuotationBoFormSchema } from "./expeditionsUpdateBoForm";
import { ConfirmationModalProps } from "@/types/confirmationModal";
import {
  MainModalProps,
  ModalContentProps,
  SideModalProps,
} from "@/types/bo/updateExpedition.type";
import { DocumentListItem } from "@/components/shared/documentListItem";
import { ReQuotationBoFormData } from "./expeditionsUpdateBoForm";
import { InfoReExpeBo } from "./infoReExpeBo";
import { sortDocumentsByPriority } from "@/constants/fileCrud";
import { getExpeditionIdentifier } from "@/lib/expedition.util";
import { useTranslation } from "react-i18next";

export const ReQuotationModal = ({ expedition, company, onClose, customs }: ModalContentProps) => {
  const [confirmationModalState, setConfirmationModalState] =
    useState<ConfirmationModalProps | null>(null);
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);

  const { mutateAsync: createQuotationBo, isPending } = useCreateQuotationBo();

  const method = useForm<ReQuotationBoFormData>({
    resolver: zodResolver(ReQuotationBoFormSchema),
    defaultValues: {
      ...QuotationUpdateBoDefaultValues({ expedition }),
      basedOnId: expedition.id,
      skipPickup: undefined as unknown as boolean,
      note: undefined,
      transportDiscountPercentage: undefined,
      overallDiscountPercentage: undefined,
      reference: `RE-EXP-C-${expedition.quotation?.identifier}`,
    },
  });

  const onSubmit = async (data: ReQuotationBoFormData) => {
    const payload = {
      billingEntityId: data.billingEntityId,
      pickUpLocationId: data.pickUpLocationId,
      deliveryContent: data.deliveryContent.map((item) => ({
        wineName: item.wineName,
        year: item.year === 0 ? undefined : Number(item.year),
        alcoholPercent: item.alcoholPercent,
        volume: item.volume,
        color: item.color,
        numberOfBottles: item.quantity,
        unitPrice: item.price,
      })),
      woodBoxes: data.woodBoxes.map((item) => ({
        bottleQuantity: item.bottleQuantity,
        boxQuantity: item.boxQuantity,
        bottleVolume: item.bottleVolume,
      })),
      lastName: data.lastName,
      firstName: data.firstName,
      email: data.email,
      phone: data.phone,
      street: data.street,
      postalCode: data.postalCode ?? "",
      city: data.city,
      companyName: data.company,
      express: data.express,
      insurance: data.insurance,
      dapOption: data.dapOption,
      reference: data.reference,
      note: data.note,
      comment: data.comment,
      skipPickup: data.skipPickup,
      basedOnId: expedition.id,
      transportDiscountPercentage: data.transportDiscountPercentage,
      overallDiscountPercentage: data.overallDiscountPercentage,
    };

    const ret = await createQuotationBo(payload);

    if (ret.status === 200 || ret.status === 201) {
      onClose();
    }
  };

  return (
    <FormProvider {...method}>
      <form
        onSubmit={method.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      >
        <div className="flex h-[80vh] w-full flex-col-reverse justify-between gap-2 overflow-y-hidden lg:flex-row">
          <QuotationMainModal
            expedition={expedition}
            customs={customs}
            company={company}
            isPending={isPending}
            setConfirmationModalState={setConfirmationModalState}
          />
          <QuotationSideModal
            expedition={expedition}
            company={company}
            onClose={onClose}
            setConfirmationModalState={setConfirmationModalState}
            handleSave={() => {}}
            isPending={isPending}
            setActionData={setActionData}
          />
        </div>
      </form>
      {confirmationModalState && (
        <ConfirmationModal
          open={!!confirmationModalState}
          onClose={() => setConfirmationModalState(null)}
          title={confirmationModalState.title}
          text={confirmationModalState.text}
          confirm={confirmationModalState.confirm}
        />
      )}
      {actionData && actionData.action === "NOTIFICATION" && (
        <NotificationModal
          expeditionId={actionData.expedition.id}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "NOTES" && (
        <NotesModal
          expedition={actionData.expedition}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
    </FormProvider>
  );
};

const QuotationSideModal = ({
  expedition,
  company,
  onClose,
  setConfirmationModalState,
  handleSave,
  setActionData,
}: SideModalProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const method = useFormContext<ReQuotationBoFormData>();

  const expeditionIdentifier = getExpeditionIdentifier(expedition);
  const hasUnreadNotif =
    (expedition.notification?.length ?? 0) > 0 &&
    expedition.notification?.some((n) => n.readBy == null);

  const client =
    expedition?.deliveryInfo?.company ||
    (expedition?.deliveryInfo?.firstName && expedition?.deliveryInfo?.lastName
      ? expedition?.deliveryInfo?.firstName + " " + expedition?.deliveryInfo?.lastName
      : "-");

  const pickupLocationWatch = method.watch("pickUpLocationId");
  const billingEntityWatch = method.watch("billingEntityId");

  const pickupLocation = company.pickUpLocations.find(
    (pickUpLocation) => pickUpLocation.id === pickupLocationWatch,
  );

  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === billingEntityWatch,
  );

  const pickupAddress =
    pickupLocation?.street + ", " + pickupLocation?.postalCode + " " + pickupLocation?.city;

  const expressWatch = method.watch("express");
  const insuranceWatch = method.watch("insurance");
  const dapOptionWatch = method.watch("dapOption");

  const sortedDocuments = useMemo(
    () => sortDocumentsByPriority(expedition.expeditionDocuments?.documents ?? []),
    [expedition.expeditionDocuments?.documents],
  );

  return (
    <div className="relative flex h-full flex-1/6 flex-col gap-0 overflow-y-auto rounded-lg bg-white p-0">
      <div className="sticky top-0 flex flex-col gap-0 border-b bg-white p-4">
        <div className="flex flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-black">{expeditionIdentifier}</h3>
          <div className="flex flex-row items-center justify-between gap-0">
            <Button
              variant="ghost"
              onClick={() => setActionData({ expedition, action: "NOTES" })}
              className="relative h-12 w-12 [&_svg]:h-8 [&_svg]:w-8"
              type="button"
            >
              <NotebookPen className="h-4 w-4" />
              <Badge variant="destructive" className="absolute top-0 right-0 h-4 w-4">
                {expedition.expeditionNotes?.length}
              </Badge>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActionData({ expedition, action: "NOTIFICATION" })}
              className={`relative h-12 w-12 [&_svg]:h-8 [&_svg]:w-8 ${hasUnreadNotif ? "bg-yellow-400 text-white hover:bg-yellow-500" : ""}`}
              type="button"
            >
              <Bell className="h-4 w-4" />
              <Badge variant="destructive" className="absolute top-0 right-0 h-4 w-4">
                {expedition.notification?.length}
              </Badge>
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleSave()}
              type="button"
              className={`h-12 w-12 [&_svg]:h-8 [&_svg]:w-8 ${expedition.processStatus === "VALIDATED" && "hidden"}`}
            >
              <Save className="text-brand600 h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              type="button"
              className="h-12 w-12 [&_svg]:h-8 [&_svg]:w-8"
            >
              <X className="h-8 w-8 text-red-600" />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex h-full flex-1/6 flex-col gap-2 overflow-y-auto rounded-lg bg-white px-4">
        <div className="flex flex-col gap-0 bg-white p-0">
          <p className="text-base font-medium text-gray-900">{client}</p>
          <p className="text-sm text-gray-600">{expedition?.deliveryInfo?.country || "-"}</p>
        </div>
        <ProtectedInformation
          label={t("reQuotation.packagingLabel")}
          value={
            <p className="text-brand600 text-sm font-medium">
              {expedition.quotation?.quotationWoodBox &&
              expedition.quotation?.quotationWoodBox.length > 0
                ? t("reQuotation.packagingWoodBox")
                : t("reQuotation.packagingStandard")}
            </p>
          }
        />
        <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">{t("reQuotation.optionsTitle")}</h4>
          </div>
          <ProtectedInformation
            label={t("reQuotation.shippingTypeLabel")}
            value={<TypeEnvoi express={expressWatch} />}
            onClick={() => {
              setConfirmationModalState({
                title: t("reQuotation.changeShippingTypeTitle"),
                text: expressWatch
                  ? t("reQuotation.changeShippingTypeToStandard")
                  : t("reQuotation.changeShippingTypeToExpress"),
                confirm: () => {
                  method.setValue("express", !expressWatch);
                },
              });
            }}
          />
          <ProtectedInformation
            label={t("reQuotation.insuranceLabel")}
            value={
              insuranceWatch ? (
                <CircleCheck className="h-6 w-6 fill-green-500 text-white" />
              ) : (
                <CircleX className="h-6 w-6 fill-red-500 text-white" />
              )
            }
            onClick={() =>
              setConfirmationModalState({
                title: t("reQuotation.changeInsuranceTitle"),
                text: insuranceWatch
                  ? t("reQuotation.disableInsuranceText")
                  : t("reQuotation.enableInsuranceText"),
                confirm: () => {
                  method.setValue("insurance", !insuranceWatch);
                },
              })
            }
          />
          <ProtectedInformation
            label={t("reQuotation.incotermLabel")}
            value={<Incoterm dapOption={dapOptionWatch} />}
            onClick={() =>
              setConfirmationModalState({
                title: t("reQuotation.changeIncotermTitle"),
                text: dapOptionWatch
                  ? t("reQuotation.disableDapText")
                  : t("reQuotation.enableDapText"),
                confirm: () => {
                  method.setValue("dapOption", !dapOptionWatch);
                },
              })
            }
          />
        </div>
        <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">
              {t("reQuotation.clientCommentTitle")}
            </h4>
            <MessageCircleMore className="fill-black text-gray-100" />
          </div>
          <p className="text-base text-gray-600">{expedition.deliveryInfo?.comment || "-"}</p>
        </div>
        <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">{t("reQuotation.documentsTitle")}</h4>
            <FileText className="fill-black text-gray-100" />
          </div>
          {sortedDocuments.length > 0 ? (
            <div className="flex flex-col">
              {sortedDocuments.map((doc) => (
                <DocumentListItem key={doc.id} file={doc} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("reQuotation.noDocuments")}</p>
          )}
        </div>
        <div id="userContainer" className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("reQuotation.userLabel")}
            value={expedition.createdBy?.firstName + " " + expedition.createdBy?.lastName}
            bold
          />
          <InformationDisplay
            label={t("reQuotation.phoneLabel")}
            value={expedition.createdBy?.mobilePhoneNumber || "-"}
          />
          <InformationDisplay
            label={t("reQuotation.emailLabel")}
            value={expedition.createdBy?.email || "-"}
          />
        </div>
        <div id="billingContainer" className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("reQuotation.billingLabel")}
            value={billingEntity?.companyName || "-"}
            bold
          />
          <InformationDisplay
            label={t("reQuotation.emailLabel")}
            value={billingEntity?.email || "-"}
          />
        </div>
        <div className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("reQuotation.pickupLabel")}
            value={pickupLocation?.name || "-"}
            bold
          />
          <InformationDisplay label={t("reQuotation.addressLabel")} value={pickupAddress || "-"} />
          <InformationDisplay
            label={t("reQuotation.phoneLabel")}
            value={pickupLocation?.contactNumber || "-"}
          />
        </div>
      </div>
    </div>
  );
};

const QuotationMainModal = ({
  expedition,
  company,
  customs,
  isPending,
  setConfirmationModalState,
}: MainModalProps) => {
  const { t } = useTranslation("boExpeditionForm");
  return (
    <div className="flex flex-1/2 flex-col gap-1 overflow-y-auto">
      <InfoClientsBo
        customs={customs}
        selectedCustomId={expedition.quotation?.customId || ""}
        destinationCountry={expedition.deliveryInfo?.country || ""}
        hasCompanyName={!!expedition.deliveryInfo?.company || false}
      />
      <InfoBottlesBo
        hasScannedInvoice={expedition.quotation?.viaImportedInvoice || false}
        reExpedition={true}
      />
      <InfoPackagingBo />
      <InfoValidationBo company={company} setConfirmationModalState={setConfirmationModalState} />
      <InfoReExpeBo />
      <Button
        variant={"outline"}
        type="submit"
        className="mt-2 w-[200px] self-end"
        disabled={isPending}
      >
        {isPending ? <Loader2 className="animate-spin" /> : t("reQuotation.submit")}
      </Button>
    </div>
  );
};
